import { useCallback, useEffect, useState } from 'react'
import { ErrorBanner, formatUtc } from '../../components/Async'
import { ApiError, artifacts, type ArtifactSummary, type PresignedRead } from '../../lib/api'

/**
 * The captures taken for this alert, and what an analyst does with them.
 *
 * Most of the time this panel says "a capture exists on the device — not uploaded", which is the
 * pull model working: the screenshot stayed on the machine because nobody had a reason to look at
 * it. Fetching is a deliberate act with a person's name on it, and the button says what it does
 * rather than pretending to be a download.
 *
 * Scoped to the alert's own occurrences rather than to the device. It used to list every capture the
 * machine had ever taken, which meant an analyst reading one alert had to work out by timestamp which
 * pictures belonged to the incident in front of them — and on a busy device that is most of them.
 * The whole device is still one click away, because "was anything captured near this?" is a real
 * question and the answer is sometimes yes when the answer for this alert is no.
 *
 * When there is nothing, this says so. It used to render nothing at all, which left three quite
 * different situations looking identical: no capture policy applies to this device, a capture was
 * taken and is still on the machine, and captures are not shown on this screen. An analyst cannot act
 * on an absence they cannot see.
 *
 * The image is loaded from a short-lived presigned URL, so the bytes never pass through the API.
 */
export function EvidencePanel({ deviceId, eventIds }: { deviceId: string; eventIds: string[] }) {
  const [items, setItems] = useState<ArtifactSummary[] | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [viewing, setViewing] = useState<{ artifact: ArtifactSummary; read: PresignedRead } | null>(null)
  const [note, setNote] = useState('')
  const [wholeDevice, setWholeDevice] = useState(false)

  // Depended on as a string rather than as the array. The caller builds that array by mapping the
  // occurrences, so it is a new identity on every render, and an array in this dependency list makes
  // the effect below re-run forever — a panel that asks the API for the same captures without stopping.
  const eventKey = eventIds.join(',')

  const load = useCallback(async () => {
    try {
      // With no occurrences there is nothing to scope to, so asking for the alert's captures would
      // ask for every capture on the device and quietly answer a different question.
      const filters = wholeDevice || eventKey === '' ? { deviceId } : { eventIds: eventKey.split(',') }
      setItems((await artifacts.list(filters)).items)
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'The captures could not be loaded.')
    }
  }, [deviceId, eventKey, wholeDevice])

  useEffect(() => {
    void load()
  }, [load])

  const run = useCallback(
    async (work: () => Promise<string>) => {
      setBusy(true)
      setFailure(null)
      setNotice(null)
      try {
        setNotice(await work())
        await load()
      } catch (error) {
        setFailure(error instanceof ApiError ? error.message : 'The request failed.')
      } finally {
        setBusy(false)
      }
    },
    [load]
  )

  const fetchCapture = (artifact: ArtifactSummary) =>
    run(async () => {
      await artifacts.request(artifact.artifactId)
      return 'Requested. The device uploads it at its next check-in.'
    })

  const view = (artifact: ArtifactSummary) =>
    run(async () => {
      setViewing({ artifact, read: await artifacts.content(artifact.artifactId) })
      return 'Opened.'
    })

  const review = (decision: string) =>
    run(async () => {
      if (!viewing) throw new ApiError(400, 'No capture is open.')
      await artifacts.review(viewing.artifact.artifactId, decision, note || undefined)
      setNote('')
      return `Recorded as ${decision}.`
    })

  const empty = items !== null && items.length === 0

  return (
    <div className="card">
      <div className="card-body">
        <h3 style={{ marginTop: 0 }}>Captures</h3>

        <ErrorBanner message={failure} />
        {notice && (
          <div className="banner success" role="status" data-testid="evidence-notice">
            {notice}
          </div>
        )}

        {empty && (
          <div className="empty" data-testid="no-captures">
            {wholeDevice
              ? 'This device has never reported a capture. No policy assigned to it takes one.'
              : 'No capture was taken for this alert.'}
            <div className="hint">
              A screenshot is only taken when a policy asks for one — the "Capture evidence when a
              prompt is flagged" template does, and the account and detection policies do not. Nothing
              failed here; there is simply no picture of this moment.
            </div>
          </div>
        )}

        {!empty && (
        <div className="table-scroll">
          <table data-testid="evidence-table">
            <thead>
              <tr>
                <th>Captured</th>
                <th>State</th>
                <th className="secondary">Size</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items?.map((artifact) => (
                <tr key={artifact.artifactId}>
                  <td>
                    {formatUtc(artifact.capturedUtc ?? artifact.reportedUtc)}
                    {artifact.width && artifact.height && (
                      <div className="muted">
                        {artifact.width}×{artifact.height}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className="pill">{artifact.state}</span>
                    {artifact.decision && <div className="muted">{artifact.decision}</div>}
                  </td>
                  <td className="muted secondary">
                    {formatBytes(artifact.storedBytes ?? artifact.declaredBytes)}
                  </td>
                  <td>
                    {artifact.state === 'reported' && (
                      <button
                        type="button"
                        onClick={() => fetchCapture(artifact)}
                        disabled={busy}
                        data-testid={`fetch-capture-${artifact.artifactId}`}
                      >
                        Fetch capture
                      </button>
                    )}
                    {artifact.state === 'requested' && <span className="muted">Waiting for the device…</span>}
                    {artifact.state === 'stored' && (
                      <button
                        type="button"
                        onClick={() => view(artifact)}
                        disabled={busy}
                        data-testid={`view-capture-${artifact.artifactId}`}
                      >
                        View
                      </button>
                    )}
                    {artifact.state === 'expired' && <span className="muted">Deleted by retention</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}

        {items === null && <div className="empty">Loading…</div>}

        {items !== null && eventKey !== '' && (
          <label className="inline-check" style={{ marginTop: '0.85rem' }}>
            <input
              type="checkbox"
              checked={wholeDevice}
              onChange={(event) => setWholeDevice(event.target.checked)}
              data-testid="captures-whole-device"
            />
            <span>Show every capture this device has taken, not only this alert's</span>
          </label>
        )}

        {viewing && (
          <div data-testid="capture-viewer" style={{ marginTop: '1rem' }}>
            <img
              src={viewing.read.url}
              alt={`Capture from ${formatUtc(viewing.artifact.capturedUtc ?? viewing.artifact.reportedUtc)}`}
              style={{ width: '100%', border: '1px solid var(--border)', borderRadius: '6px' }}
            />
            <div className="hint">
              This link expires {formatUtc(viewing.read.expiresUtc)}. It reads the image directly from
              storage, so it is a grant as well as a URL.
            </div>

            <div className="field">
              <label htmlFor="review-note">What this shows</label>
              <input
                id="review-note"
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="Internal test data, not a customer list."
                data-testid="review-note"
              />
            </div>

            <div className="toolbar">
              <button type="button" onClick={() => review('benign')} disabled={busy} data-testid="review-benign">
                Benign
              </button>
              <button type="button" onClick={() => review('confirmed')} disabled={busy} data-testid="review-confirmed">
                Confirmed
              </button>
              <button type="button" onClick={() => review('inconclusive')} disabled={busy}>
                Inconclusive
              </button>
              <button type="button" onClick={() => setViewing(null)} disabled={busy}>
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function formatBytes(bytes: number | null): string {
  if (bytes === null) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
